import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  FileText, Plus, Search, ArrowLeft, Brain, Sparkles, Send, Copy, RefreshCw,
  Edit, Trash2, X, Check, ChevronRight, Mail, Users, Clock, Shield,
  BookOpen, DollarSign, CheckCircle2, AlertTriangle, Eye, Download, Share2,
  FolderKanban, Calendar, ChevronDown, Loader2, Printer
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { rfpAPI, projectAPI } from '../api';
import { Modal, Avatar, StatusBadge, Loader, EmptyState } from '../App.core';
import { formatDate, formatRelative, ROLE_LABELS } from '../utils/helpers';

const renderMd = (text) => {
  if (!text) return '';
  return text
    .replace(/^#### (.*$)/gim, '<h5 style="font-weight:600;margin:12px 0 6px;font-size:0.88rem;">$1</h5>')
    .replace(/^### (.*$)/gim, '<h4 style="font-weight:600;margin:14px 0 6px;font-size:0.92rem;">$1</h4>')
    .replace(/^## (.*$)/gim, '<h3 style="font-weight:650;margin:18px 0 8px;font-size:1rem;color:var(--px-text);">$1</h3>')
    .replace(/^# (.*$)/gim, '<h2 style="font-weight:700;margin:20px 0 10px;font-size:1.1rem;">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.*$)/gim, '<div style="padding-left:16px;margin:3px 0;"><span style="color:var(--px-primary);margin-right:6px;">•</span>$1</div>')
    .replace(/^\d+\. (.*$)/gim, '<div style="padding-left:16px;margin:3px 0;">$1</div>')
    .replace(/\|(.+)\|/g, (match) => {
      const cells = match.split('|').filter(c => c.trim());
      if (cells.every(c => c.trim().match(/^[-:]+$/))) return '';
      return `<div style="display:flex;gap:8px;padding:4px 0;border-bottom:1px solid var(--px-border);font-size:0.82rem;">${cells.map(c => `<span style="flex:1;">${c.trim()}</span>`).join('')}</div>`;
    })
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
};

const STATUS_COLORS = { draft: '#64748b', review: '#f59e0b', approved: '#059669', issued: '#2563eb', closed: '#6b7280', cancelled: '#ef4444' };

// =============================================
// RFP LIST PAGE
// =============================================
export const RFPListPage = () => {
  const [rfps, setRfps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '', rfp_type: '' });
  const [updatingRfpId, setUpdatingRfpId] = useState(null);
  const { canCreateProject } = useAuth();
  const navigate = useNavigate();

  const loadRfps = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await rfpAPI.list({ limit: 100 });
      setRfps(data.rfps || []);
    } catch {
      toast.error('Failed to load RFPs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRfps();
  }, [loadRfps]);

  const handleStatusChange = async (rfpId, newStatus) => {
    const prevRfps = [...rfps];
    setRfps(prev => prev.map(r => (r.id === rfpId ? { ...r, status: newStatus } : r)));
    setUpdatingRfpId(rfpId);
    try {
      await rfpAPI.update(rfpId, { status: newStatus });
      toast.success('RFP status updated');
      loadRfps();
    } catch (err) {
      setRfps(prevRfps);
      toast.error(err.response?.data?.error || 'Failed to update RFP status');
    } finally {
      setUpdatingRfpId(null);
    }
  };

  const filteredRfps = rfps.filter(r => {
    if (filters.status && r.status !== filters.status) return false;
    if (filters.rfp_type && r.rfp_type !== filters.rfp_type) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const matchTitle = r.title?.toLowerCase().includes(q);
      const matchNum = r.rfp_number?.toLowerCase().includes(q);
      const matchProj = r.project?.name?.toLowerCase().includes(q) || r.project?.project_code?.toLowerCase().includes(q);
      if (!matchTitle && !matchNum && !matchProj) return false;
    }
    return true;
  });

  const totalCount = rfps.length;
  const draftCount = rfps.filter(r => r.status === 'draft').length;
  const inReviewCount = rfps.filter(r => r.status === 'review').length;
  const approvedOrIssuedCount = rfps.filter(r => r.status === 'approved' || r.status === 'issued').length;

  return (
    <>
      {/* Top Banner matching Dashboard and Site Activities */}
      <div className="dashboard-welcome-banner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 className="dashboard-welcome-title">RFP Management</h2>
          <div className="dashboard-welcome-subtitle">
            <span>AI-powered Request for Proposal generation, vendor distribution, and procurement packages</span>
          </div>
        </div>
        {canCreateProject() && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} />
            <span>New RFP Package</span>
          </button>
        )}
      </div>

      {/* KPI Cards Grid */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-info">
          <div className="kpi-card-header">
            <span className="kpi-label">Total RFP Packages</span>
            <div className="kpi-card-icon-pill">
              <FileText size={16} />
            </div>
          </div>
          <div className="kpi-value">{totalCount}</div>
          <div className="kpi-card-footer">
            <span>Across all programmes</span>
          </div>
        </div>

        <div className="kpi-card kpi-muted">
          <div className="kpi-card-header">
            <span className="kpi-label">Draft Packages</span>
            <div className="kpi-card-icon-pill">
              <Clock size={16} />
            </div>
          </div>
          <div className="kpi-value">{draftCount}</div>
          <div className="kpi-card-footer">
            <span>In composition stage</span>
          </div>
        </div>

        <div className="kpi-card kpi-primary">
          <div className="kpi-card-header">
            <span className="kpi-label">Under Technical Review</span>
            <div className="kpi-card-icon-pill">
              <Eye size={16} />
            </div>
          </div>
          <div className="kpi-value">{inReviewCount}</div>
          <div className="kpi-card-footer">
            <span>Awaiting sign-off</span>
          </div>
        </div>

        <div className="kpi-card kpi-success">
          <div className="kpi-card-header">
            <span className="kpi-label">Approved & Issued</span>
            <div className="kpi-card-icon-pill">
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div className="kpi-value">{approvedOrIssuedCount}</div>
          <div className="kpi-card-footer">
            <span>Shared with vendors</span>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar matching Projects / Site Activities */}
      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={16} className="search-input-icon" />
          <input
            className="search-input"
            placeholder="Search RFPs by title, number, or project..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="issued">Issued</option>
              <option value="closed">Closed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>

          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              value={filters.rfp_type}
              onChange={e => setFilters(f => ({ ...f, rfp_type: e.target.value }))}
            >
              <option value="">All RFP Types</option>
              <option value="combined">Combined (Tech + Comm)</option>
              <option value="technical">Technical Only</option>
              <option value="commercial">Commercial Only</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>
        </div>
      </div>

      {/* RFP Table Card matching My Activities */}
      {loading ? (
        <Loader />
      ) : (
        <div className="card">
          {filteredRfps.length === 0 ? (
            <div style={{ padding: '20px 0' }}>
              <EmptyState
                icon={FileText}
                title="No RFPs found"
                message="Create your first AI-powered Request for Proposal package to begin procurement."
                action={
                  canCreateProject() && (
                    <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                      <Plus size={16} /> Create RFP
                    </button>
                  )
                }
              />
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>RFP Reference</th>
                    <th>Package Title</th>
                    <th>Project Programme</th>
                    <th>Type</th>
                    <th>Submission Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRfps.map(r => {
                    const overdue = r.submission_deadline && new Date(r.submission_deadline) < new Date() && r.status !== 'approved' && r.status !== 'closed';
                    return (
                      <tr
                        key={r.id}
                        className="clickable"
                        onClick={() => navigate(`/rfps/${r.id}`)}
                      >
                        <td style={{ width: 160 }} onClick={e => e.stopPropagation()}>
                          <div className="task-status-dropdown-wrap">
                            <select
                              value={r.status}
                              onChange={e => handleStatusChange(r.id, e.target.value)}
                              disabled={updatingRfpId === r.id}
                              className={`task-status-dropdown status-${r.status}`}
                            >
                              <option value="draft">Draft</option>
                              <option value="review">Under Review</option>
                              <option value="approved">Approved</option>
                              <option value="issued">Issued</option>
                              <option value="closed">Closed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                            {updatingRfpId === r.id ? (
                              <Loader2 size={12} className="task-status-spinner" />
                            ) : (
                              <ChevronDown size={12} className="task-status-chevron" />
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="rfp-number-code">
                            {r.rfp_number}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 600, color: '#0B1220' }}>{r.title}</span>
                            {overdue && (
                              <span title="Deadline Passed" style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <AlertTriangle size={14} style={{ color: 'var(--px-danger)' }} />
                              </span>
                            )}
                          </div>
                          {r.description ? (
                            <div style={{ fontSize: '0.76rem', color: 'var(--px-text-muted)', marginTop: 2, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.description}
                            </div>
                          ) : r.creator ? (
                            <div style={{ fontSize: '0.76rem', color: 'var(--px-text-muted)', marginTop: 2 }}>
                              Lead: {r.creator.first_name} {r.creator.last_name}
                              {r.vendor_shares && r.vendor_shares.length > 0 && ` • ${r.vendor_shares.length} vendor(s) invited`}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {r.project ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FolderKanban size={14} style={{ color: '#0038A7' }} />
                              <span style={{ fontWeight: 550, color: '#0B1220', fontSize: '0.85rem' }}>{r.project.name}</span>
                              {r.project.project_code && (
                                <span className="project-full-code" style={{ fontSize: '0.72rem', padding: '1px 5px' }}>
                                  {r.project.project_code}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--px-text-muted)' }}>—</span>
                          )}
                        </td>
                        <td>
                          <span className="badge badge-active" style={{ textTransform: 'capitalize', fontSize: '0.74rem' }}>
                            {r.rfp_type}
                          </span>
                        </td>
                        <td>
                          {r.submission_deadline ? (
                            <span className={`task-due-badge ${overdue ? 'overdue' : ''}`}>
                              <Calendar size={12} />
                              <span>{formatDate(r.submission_deadline)}</span>
                            </span>
                          ) : (
                            <span style={{ color: 'var(--px-text-muted)' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <CreateRFPModal
          onClose={() => setShowCreate(false)}
          onSuccess={(id) => {
            setShowCreate(false);
            navigate(`/rfps/${id}`);
          }}
        />
      )}
    </>
  );
};

// =============================================
// CREATE RFP MODAL
// =============================================
const CreateRFPModal = ({ onClose, onSuccess }) => {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    project_id: '',
    title: '',
    rfp_number: '',
    rfp_type: 'combined',
    description: '',
    submission_deadline: ''
  });
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    projectAPI.list({ limit: 100 }).then(({ data }) => setProjects(data.projects || [])).catch(() => { });
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.project_id) errs.project_id = 'Please select an associated project.';
    if (!form.title.trim()) errs.title = 'RFP title is required.';
    if (!form.rfp_number.trim()) errs.rfp_number = 'RFP reference number is required.';
    return errs;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    setSaving(true);
    try {
      const { data } = await rfpAPI.create(form);
      toast.success('RFP package created with 22 professional sections.');
      onSuccess(data.rfp.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create RFP.');
    } finally {
      setSaving(false);
    }
  };

  const set = (f) => (e) => {
    setFieldErrors(prev => {
      const copy = { ...prev };
      delete copy[f];
      return copy;
    });
    setForm(prev => ({ ...prev, [f]: e.target.value }));
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      showClose={false}
      size="modal-lg"
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
              color: '#0038A7',
              border: '1px solid rgba(0, 56, 167, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <FileText size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.3px' }}>
              Create New RFP Package
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>
              Initialize a comprehensive 22-section procurement skeleton with AI generation support
            </p>
          </div>
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving} style={{ minWidth: 130 }}>
            {saving ? 'Creating...' : 'Create RFP Package'}
          </button>
        </div>
      }
    >

      <div className="form-group">
        <label className="form-label">Associated Project Programme <span className="required">*</span></label>
        <div className="form-select-wrapper">
          <select
            className={`form-select ${fieldErrors.project_id ? 'field-error' : ''}`}
            value={form.project_id}
            onChange={set('project_id')}
          >
            <option value="">Select project programme...</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.project_code})
              </option>
            ))}
          </select>
          <ChevronDown size={14} className="form-select-chevron" />
        </div>
        {fieldErrors.project_id && (
          <div style={{ color: '#DC2626', fontSize: '0.78rem', marginTop: 4, fontWeight: 500 }}>
            {fieldErrors.project_id}
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">RFP Package Title <span className="required">*</span></label>
          <input
            className={`form-input ${fieldErrors.title ? 'field-error' : ''}`}
            value={form.title}
            onChange={set('title')}
            placeholder="e.g. MEP Subcontractor Works — Al Noor Tower"
          />
          {fieldErrors.title && (
            <div style={{ color: '#DC2626', fontSize: '0.78rem', marginTop: 4, fontWeight: 500 }}>
              {fieldErrors.title}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">RFP Reference Number <span className="required">*</span></label>
          <input
            className={`form-input ${fieldErrors.rfp_number ? 'field-error' : ''}`}
            value={form.rfp_number}
            onChange={set('rfp_number')}
            placeholder="e.g. RFP-ANT-MEP-001"
          />
          {fieldErrors.rfp_number && (
            <div style={{ color: '#DC2626', fontSize: '0.78rem', marginTop: 4, fontWeight: 500 }}>
              {fieldErrors.rfp_number}
            </div>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Package Scope Type</label>
          <div className="form-select-wrapper">
            <select className="form-select" value={form.rfp_type} onChange={set('rfp_type')}>
              <option value="combined">Combined (Technical + Commercial)</option>
              <option value="technical">Technical Specification Only</option>
              <option value="commercial">Commercial / Financial Only</option>
            </select>
            <ChevronDown size={14} className="form-select-chevron" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Submission Deadline</label>
          <input
            className="form-input"
            type="date"
            value={form.submission_deadline}
            onChange={set('submission_deadline')}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Scope Description & Summary</label>
        <textarea
          className="form-textarea"
          value={form.description}
          onChange={set('description')}
          placeholder="Brief description of work package, procurement scope, and trade deliverables..."
          rows={3}
        />
      </div>
    </Modal>
  );
};

// =============================================
// RFP BUILDER / EDITOR PAGE
// =============================================
export const RFPBuilderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rfp, setRfp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showTeamInputs, setShowTeamInputs] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [savingSection, setSavingSection] = useState(false);
  const contentRef = useRef(null);

  const loadRFP = useCallback(async () => {
    try {
      const { data } = await rfpAPI.getById(id);
      setRfp(data.rfp);
      const sections = data.rfp.sections || [];
      const techSections = sections
        .filter(s => s.section_category === 'technical')
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      const sortedSections = [...sections].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      const firstSection = techSections[0] || sortedSections[0];

      if (!activeSection && firstSection) {
        setActiveSection(firstSection);
      } else if (activeSection) {
        const updated = sections.find(s => s.id === activeSection.id);
        if (updated) setActiveSection(updated);
      }
    } catch (err) { toast.error('Failed to load RFP'); navigate('/rfps'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { loadRFP(); }, [loadRFP]);

  const [sectionFilter, setSectionFilter] = useState('');

  const technicalSections = (rfp?.sections || [])
    .filter(s => s.section_category === 'technical')
    .filter(s => !sectionFilter || s.title.toLowerCase().includes(sectionFilter.toLowerCase()))
    .sort((a, b) => a.sort_order - b.sort_order);

  const commercialSections = (rfp?.sections || [])
    .filter(s => s.section_category === 'commercial')
    .filter(s => !sectionFilter || s.title.toLowerCase().includes(sectionFilter.toLowerCase()))
    .sort((a, b) => a.sort_order - b.sort_order);

  const totalTechnical = (rfp?.sections || []).filter(s => s.section_category === 'technical').length;
  const filledTechnical = (rfp?.sections || []).filter(s => s.section_category === 'technical' && s.content?.trim()).length;
  const totalCommercial = (rfp?.sections || []).filter(s => s.section_category === 'commercial').length;
  const filledCommercial = (rfp?.sections || []).filter(s => s.section_category === 'commercial' && s.content?.trim()).length;

  const handleSaveSection = async () => {
    if (!activeSection) return;
    setSavingSection(true);
    try {
      await rfpAPI.updateSection(rfp.id, activeSection.id, { content: editingContent });
      toast.success('Section saved.');
      await loadRFP();
      setIsEditing(false);
    } catch (err) { toast.error('Failed to save.'); }
    finally { setSavingSection(false); }
  };

  const handleRegenerate = async () => {
    if (!aiInstructions.trim()) { toast.error('Please provide instructions for the AI.'); return; }
    setRegenerating(true);
    try {
      await rfpAPI.regenerateSection(rfp.id, activeSection.id, { instructions: aiInstructions });
      toast.success('Section regenerated by AI.');
      setAiInstructions('');
      await loadRFP();
    } catch (err) { toast.error(err.response?.data?.error || 'AI regeneration failed.'); }
    finally { setRegenerating(false); }
  };

  const handleGenerateAll = async (teamInputs) => {
    setGenerating(true);
    setShowTeamInputs(false);
    try {
      const { data } = await rfpAPI.generateWithAI(rfp.id, { team_inputs: teamInputs });
      toast.success(`AI generated ${data.sections_updated} sections!`);
      await loadRFP();
    } catch (err) { toast.error(err.response?.data?.error || 'AI generation failed. Check Gemini API key.'); }
    finally { setGenerating(false); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await rfpAPI.update(rfp.id, { status: newStatus });
      toast.success(`RFP status updated to ${newStatus}.`);
      await loadRFP();
    } catch (err) { toast.error('Failed to update status.'); }
  };

  const startEditing = () => { setEditingContent(activeSection?.content || ''); setIsEditing(true); };

  const filledCount = (rfp?.sections || []).filter(s => s.content && s.content.trim().length > 20).length;
  const totalCount = (rfp?.sections || []).length;
  const percentComplete = totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0;

  if (loading) return <Loader />;
  if (!rfp) return null;

  return (
    <div className="rfp-detail-container">
      {/* Top Header Card */}
      <div className="rfp-detail-top-card">
        <div className="rfp-detail-header-main">
          <div>
            <Link to="/rfps" className="rfp-detail-breadcrumb">
              <ArrowLeft size={14} /> Back to RFP Management
            </Link>
            <div className="rfp-detail-title-wrap">
              <h1 className="rfp-detail-title">{rfp.title}</h1>
              <span className="rfp-detail-number-badge">{rfp.rfp_number}</span>
              <span
                className="badge"
                style={{
                  background: `${STATUS_COLORS[rfp.status]}18`,
                  color: STATUS_COLORS[rfp.status],
                  border: `1px solid ${STATUS_COLORS[rfp.status]}35`,
                  fontWeight: 650
                }}
              >
                {rfp.status?.toUpperCase()}
              </span>
            </div>
            <div className="rfp-detail-submeta">
              <span className="rfp-detail-submeta-item">
                <FolderKanban size={14} style={{ color: '#0038A7' }} />
                <strong>{rfp.project?.name || 'Unassigned Project'}</strong>
              </span>
              {rfp.submission_deadline && (
                <span className="rfp-detail-submeta-item">
                  <Calendar size={14} style={{ color: '#64748b' }} />
                  Deadline: {formatDate(rfp.submission_deadline)}
                </span>
              )}
              <span className="rfp-detail-submeta-item">
                <BookOpen size={14} style={{ color: '#64748b' }} />
                {filledCount} of {totalCount} sections drafted ({percentComplete}%)
              </span>
            </div>
          </div>

          <div className="rfp-detail-actions">
            <button
              className="btn btn-primary"
              onClick={() => setShowTeamInputs(true)}
              disabled={generating}
            >
              <Brain size={15} /> {generating ? 'Generating...' : 'Generate with Propel AI'}
            </button>

            <button
              className="btn btn-secondary"
              onClick={() => setShowPreview(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                borderColor: '#cbd5e1'
              }}
              title="Preview full RFP Document"
            >
              <Eye size={15} /> Preview
            </button>

            <div className="filter-select-wrapper" style={{ margin: 0 }}>
              <select
                className="filter-select"
                value={rfp.status}
                onChange={e => handleStatusChange(e.target.value)}
                style={{ minWidth: 140, fontWeight: 500 }}
              >
                <option value="draft">Draft</option>
                <option value="review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="issued">Issued to Vendors</option>
                <option value="closed">Closed</option>
              </select>
              <ChevronDown size={14} className="filter-select-chevron" />
            </div>

            <button className="btn btn-secondary" onClick={() => setShowShareModal(true)}>
              <Share2 size={15} /> Share RFP
            </button>

            <button className="btn btn-secondary" onClick={() => navigate(`/rfps/${rfp.id}/proposals`)}>
              Proposals ({(rfp.proposals || []).length})
            </button>
          </div>
        </div>

        {/* Progress Strip */}
        <div className="rfp-detail-progress-strip">
          <span className="rfp-progress-text">Drafting Completion</span>
          <div className="rfp-progress-bar-bg">
            <div className="rfp-progress-bar-fill" style={{ width: `${percentComplete}%` }} />
          </div>
          <span className="rfp-progress-text">{percentComplete}%</span>
        </div>
      </div>

      {/* Generating Alert Card */}
      {generating && (
        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: 40,
            background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
            border: '1px solid #bfdbfe'
          }}
        >
          <div className="spinner" style={{ margin: '0 auto 16px', borderTopColor: '#0038A7' }} />
          <h3 style={{ fontWeight: 700, fontSize: '1.1rem', color: '#0b1220', marginBottom: 6 }}>
            Propel AI is synthesizing your RFP package...
          </h3>
          <p style={{ fontSize: '0.86rem', color: 'var(--px-text-secondary)', maxWidth: 560, margin: '0 auto' }}>
            Drafting all 22 technical and commercial clauses aligned with FIDIC standards, project specifications, and your procurement requirements.
          </p>
        </div>
      )}

      {/* Two-Column Editor Layout */}
      {!generating && (
        <div className="rfp-workspace-grid">
          {/* Navigation Panel */}
          <div className="rfp-nav-panel">
            <div className="rfp-nav-header">
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Filter sections..."
                  className="rfp-nav-search-input"
                  value={sectionFilter}
                  onChange={e => setSectionFilter(e.target.value)}
                />
              </div>
            </div>

            <div className="rfp-nav-list">
              {/* Technical Group */}
              <div className="rfp-nav-category-header tech">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <BookOpen size={13} /> Technical Specifications
                </span>
                <span style={{ fontSize: '0.72rem', opacity: 0.85 }}>
                  {filledTechnical}/{totalTechnical}
                </span>
              </div>

              {technicalSections.map(s => {
                const isActive = activeSection?.id === s.id;
                const isDone = s.content && s.content.trim().length > 0;
                return (
                  <div
                    key={s.id}
                    className={`rfp-nav-item ${isActive ? 'active-tech' : ''}`}
                    style={{ opacity: savingSection ? 0.6 : 1, cursor: savingSection ? 'not-allowed' : 'pointer' }}
                    onClick={() => { if (savingSection) return; setActiveSection(s); setIsEditing(false); setAiInstructions(''); }}
                  >
                    {isDone ? (
                      <CheckCircle2 size={15} style={{ color: '#059669', marginTop: 1, flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 15, height: 15, borderRadius: '50%', border: '1.5px dashed #cbd5e1', marginTop: 1, flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="rfp-nav-item-title">{s.title}</div>
                      {s.is_ai_generated && (
                        <span style={{ fontSize: '0.68rem', color: '#0038A7', display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2, fontWeight: 500 }}>
                          <Sparkles size={10} /> AI Assisted
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Commercial Group */}
              <div className="rfp-nav-category-header comm" style={{ marginTop: 8, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <DollarSign size={13} /> Commercial & Contractual
                </span>
                <span style={{ fontSize: '0.72rem', opacity: 0.85 }}>
                  {filledCommercial}/{totalCommercial}
                </span>
              </div>

              {commercialSections.map(s => {
                const isActive = activeSection?.id === s.id;
                const isDone = s.content && s.content.trim().length > 0;
                return (
                  <div
                    key={s.id}
                    className={`rfp-nav-item ${isActive ? 'active-comm' : ''}`}
                    style={{ opacity: savingSection ? 0.6 : 1, cursor: savingSection ? 'not-allowed' : 'pointer' }}
                    onClick={() => { if (savingSection) return; setActiveSection(s); setIsEditing(false); setAiInstructions(''); }}
                  >
                    {isDone ? (
                      <CheckCircle2 size={15} style={{ color: '#059669', marginTop: 1, flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 15, height: 15, borderRadius: '50%', border: '1.5px dashed #cbd5e1', marginTop: 1, flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="rfp-nav-item-title">{s.title}</div>
                      {s.is_ai_generated && (
                        <span style={{ fontSize: '0.68rem', color: '#0038A7', display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2, fontWeight: 500 }}>
                          <Sparkles size={10} /> AI Assisted
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Editor Panel */}
          <div className="rfp-editor-panel">
            {!activeSection ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 40, color: '#94a3b8' }}>
                <FileText size={48} style={{ opacity: 0.25, marginBottom: 12 }} />
                <p style={{ fontSize: '0.95rem', fontWeight: 600, color: '#475569' }}>Select a section to view or edit</p>
                <p style={{ fontSize: '0.82rem' }}>Choose any technical or commercial section from the left navigation index.</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="rfp-editor-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span
                      className="badge"
                      style={{
                        background: activeSection.section_category === 'technical' ? '#eff6ff' : '#fffbeb',
                        color: activeSection.section_category === 'technical' ? '#0038A7' : '#92400e',
                        border: `1px solid ${activeSection.section_category === 'technical' ? '#bfdbfe' : '#fde68a'}`,
                        fontWeight: 650,
                        textTransform: 'uppercase',
                        fontSize: '0.72rem'
                      }}
                    >
                      {activeSection.section_category}
                    </span>
                    <h3 style={{ fontSize: '1.08rem', fontWeight: 700, color: '#0b1220', margin: 0 }}>
                      {activeSection.title}
                    </h3>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {!isEditing ? (
                      <button className="btn btn-secondary btn-sm" onClick={startEditing} disabled={savingSection}>
                        <Edit size={14} /> Edit Clause
                      </button>
                    ) : (
                      <>
                        <button className="btn btn-ghost btn-sm" onClick={() => setIsEditing(false)} disabled={savingSection}>
                          Cancel
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleSaveSection}
                          disabled={savingSection}
                          style={{
                            minWidth: 100,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            background: 'linear-gradient(135deg, #0038A7, #002266)'
                          }}
                        >
                          {savingSection ? (
                            <>
                              <Loader2 size={14} className="spinning" /> Saving...
                            </>
                          ) : (
                            <>
                              <Check size={14} /> Save Clause
                            </>
                          )}
                        </button>
                      </>
                    )}
                    <button
                      className="btn btn-ghost btn-sm btn-icon"
                      title="Copy content"
                      disabled={savingSection}
                      onClick={() => {
                        if (activeSection?.content) {
                          navigator.clipboard.writeText(activeSection.content);
                          toast.success('Section content copied to clipboard');
                        }
                      }}
                    >
                      <Copy size={15} />
                    </button>
                  </div>
                </div>

                {/* Body Area */}
                <div className="rfp-editor-body" ref={contentRef} style={{ position: 'relative' }}>
                  {savingSection && (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(255, 255, 255, 0.75)',
                        backdropFilter: 'blur(2px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                        zIndex: 10
                      }}
                    >
                      <Loader2 size={28} className="spinning" style={{ color: '#0038A7' }} />
                      <span style={{ fontSize: '0.86rem', fontWeight: 600, color: '#0b1220' }}>Saving clause updates...</span>
                    </div>
                  )}

                  {isEditing ? (
                    <textarea
                      value={editingContent}
                      onChange={e => setEditingContent(e.target.value)}
                      className="rfp-editor-textarea"
                      placeholder="Write or paste section content in Markdown format..."
                      disabled={savingSection}
                    />
                  ) : activeSection.content?.trim() ? (
                    <div
                      style={{ fontSize: '0.9rem', lineHeight: 1.8, color: '#1e293b' }}
                      dangerouslySetInnerHTML={{ __html: renderMd(activeSection.content) }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                      <FileText size={40} style={{ opacity: 0.2, marginBottom: 12 }} />
                      <p style={{ fontSize: '0.95rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>This clause has not been drafted yet</p>
                      <p style={{ fontSize: '0.84rem', color: '#64748b', maxWidth: 440, margin: '0 auto' }}>
                        Click "Generate with Propel AI" at the top to draft all sections at once, or use the prompt bar below to draft this specific clause.
                      </p>
                    </div>
                  )}
                </div>

                {/* AI Section Refinement Bar */}
                {!isEditing && (
                  <div className="rfp-ai-prompt-box">
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: 'linear-gradient(135deg, #0038A7, #002266)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        flexShrink: 0
                      }}
                    >
                      <Sparkles size={16} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 650, color: '#0038A7', marginBottom: 5 }}>
                        Propel AI Clause Refinement & Enhancement
                      </div>
                      <textarea
                        value={aiInstructions}
                        onChange={e => setAiInstructions(e.target.value)}
                        placeholder={`Instruct AI to draft or enhance "${activeSection.title}" (e.g. "Add FIDIC 1999 Red Book Sub-Clause 4.12 conditions", "Include strict QA/QC submission criteria within 14 days")...`}
                        style={{
                          width: '100%',
                          padding: '9px 12px',
                          border: '1px solid #cbd5e1',
                          borderRadius: 7,
                          fontSize: '0.84rem',
                          minHeight: 48,
                          resize: 'vertical',
                          outline: 'none',
                          background: '#ffffff',
                          color: '#0b1220'
                        }}
                      />
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleRegenerate}
                      disabled={regenerating || !aiInstructions.trim()}
                      style={{
                        marginTop: 22,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <RefreshCw size={13} className={regenerating ? 'spinning' : ''} />
                      {regenerating ? 'Drafting...' : 'Refine Clause'}
                    </button>
                  </div>
                )}

                {/* Footer Metadata */}
                <div className="rfp-editor-footer">
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                    {activeSection.is_ai_generated && (
                      <span style={{ color: '#0038A7', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 550 }}>
                        <Sparkles size={12} /> AI Assisted Clause
                      </span>
                    )}
                    {activeSection.last_edited_at && (
                      <span>Last updated {formatRelative(activeSection.last_edited_at)}</span>
                    )}
                    {activeSection.editor && (
                      <span>by {activeSection.editor.first_name} {activeSection.editor.last_name}</span>
                    )}
                  </div>
                  {/* <div style={{ fontFamily: 'var(--px-font-mono)', fontSize: '0.74rem' }}>
                    Clause ID: {activeSection.id}
                  </div> */}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Vendor Shares Bar */}
      {rfp.vendor_shares?.length > 0 && (
        <div
          style={{
            padding: '12px 18px',
            background: '#ffffff',
            borderRadius: 10,
            border: '1px solid var(--px-border)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
            flexWrap: 'wrap',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
          }}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', fontWeight: 650, color: '#0b1220' }}>
            <Mail size={15} style={{ color: '#0038A7' }} />
            Shared with {rfp.vendor_shares.length} Vendor(s):
          </div>
          {rfp.vendor_shares.map(v => (
            <span
              key={v.id}
              className="badge"
              style={{
                background: '#eff6ff',
                color: '#1d4ed8',
                border: '1px solid #bfdbfe',
                fontSize: '0.76rem',
                padding: '3px 8px'

              }}
            >
              {v.vendor_name} {v.vendor_company ? `(${v.vendor_company})` : ''} · {v.vendor_email}
            </span>
          ))}
        </div>
      )}

      {/* Modals */}
      {showTeamInputs && <TeamInputsModal onClose={() => setShowTeamInputs(false)} onSubmit={handleGenerateAll} rfp={rfp} />}
      {showShareModal && <ShareVendorModal rfpId={rfp.id} onClose={() => setShowShareModal(false)} onSuccess={loadRFP} />}
      {showPreview && <RFPPreviewModal rfp={rfp} onClose={() => setShowPreview(false)} />}
    </div>
  );
};

// =============================================
// TEAM INPUTS MODAL (Before AI generation)
// =============================================
const TeamInputsModal = ({ onClose, onSubmit, rfp }) => {
  const [inputs, setInputs] = useState({
    work_packages: rfp?.team_inputs?.work_packages || '',
    special_requirements: rfp?.team_inputs?.special_requirements || '',
    contract_form: rfp?.team_inputs?.contract_form || 'FIDIC Red Book',
    prequalification_requirements: rfp?.team_inputs?.prequalification_requirements || '',
    budget_range: rfp?.team_inputs?.budget_range || '',
    timeline_constraints: rfp?.team_inputs?.timeline_constraints || '',
    local_requirements: rfp?.team_inputs?.local_requirements || '',
    sustainability_targets: rfp?.team_inputs?.sustainability_targets || '',
    additional_notes: rfp?.team_inputs?.additional_notes || '',
  });

  const set = (f) => (e) => setInputs(prev => ({ ...prev, [f]: e.target.value }));

  return (
    <Modal
      isOpen
      onClose={onClose}
      showClose={false}
      size="modal-lg"
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
              color: '#0038A7',
              border: '1px solid rgba(0, 56, 167, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Brain size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.3px' }}>
              Team Inputs for AI Generation
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>
              Provide project-specific inputs to guide Propel AI across all 22 technical and commercial sections
            </p>
          </div>
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => onSubmit(inputs)}
            style={{ minWidth: 140 }}
          >
            <Brain size={15} /> Generate RFP with AI
          </button>
        </div>
      }
    >

      <div className="form-group">
        <label className="form-label">Work Packages / Scope Details</label>
        <textarea
          className="form-textarea"
          value={inputs.work_packages}
          onChange={set('work_packages')}
          placeholder="e.g. MEP works including HVAC, electrical, plumbing, fire fighting. Excludes structural and civil works."
          rows={3}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Contract Form</label>
          <div className="filter-select-wrapper" style={{ margin: 0 }}>
            <select className="form-select" value={inputs.contract_form} onChange={set('contract_form')}>
              <option>FIDIC Red Book</option>
              <option>FIDIC Yellow Book</option>
              <option>FIDIC Silver Book</option>
              <option>JCT Standard</option>
              <option>NEC4 ECC</option>
              <option>Bespoke / Custom</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Budget Range (Optional)</label>
          <input
            className="form-input"
            value={inputs.budget_range}
            onChange={set('budget_range')}
            placeholder="e.g. 15M - 25M"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Special Technical Requirements</label>
        <textarea
          className="form-textarea"
          value={inputs.special_requirements}
          onChange={set('special_requirements')}
          placeholder="e.g. District cooling connection required, seismic zone 2A design, BIM Level 2 mandatory"
          rows={2}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Prequalification Requirements</label>
        <textarea
          className="form-textarea"
          value={inputs.prequalification_requirements}
          onChange={set('prequalification_requirements')}
            placeholder="e.g. Minimum 3 similar projects in last 5 years, minimum 50M single project value"
          rows={2}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Timeline Constraints</label>
          <input
            className="form-input"
            value={inputs.timeline_constraints}
            onChange={set('timeline_constraints')}
            placeholder="e.g. 18 months from award, sectional completion at L10"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Local Regulatory Requirements</label>
          <input
            className="form-input"
            value={inputs.local_requirements}
            onChange={set('local_requirements')}
            placeholder="e.g. Abu Dhabi QCC, ADCD requirements, Estidama Pearl 2"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Sustainability / Green Building Targets</label>
        <input
          className="form-input"
          value={inputs.sustainability_targets}
          onChange={set('sustainability_targets')}
          placeholder="e.g. LEED Gold, Estidama 2 Pearl, 30% recycled materials"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Additional Notes for AI</label>
        <textarea
          className="form-textarea"
          value={inputs.additional_notes}
          onChange={set('additional_notes')}
          placeholder="Any other instructions or trade conditions for the AI when synthesizing the RFP..."
          rows={2}
        />
      </div>
    </Modal>
  );
};

// =============================================
// SHARE WITH VENDOR MODAL
// =============================================
const ShareVendorModal = ({ rfpId, onClose, onSuccess }) => {
  const [form, setForm] = useState({ vendor_name: '', vendor_email: '', vendor_company: '', contact_person: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleShare = async () => {
    if (!form.vendor_name || !form.vendor_email) { toast.error('Vendor name and email are required.'); return; }
    setSaving(true);
    try {
      await rfpAPI.shareWithVendor(rfpId, form);
      toast.success('RFP shared with vendor! Invitation email sent.');
      onSuccess();
      onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to share.'); }
    finally { setSaving(false); }
  };

  const set = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  return (
    <Modal
      isOpen
      onClose={onClose}
      showClose={false}
      size="modal-md"
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
              color: '#0038A7',
              border: '1px solid rgba(0, 56, 167, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Share2 size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.3px' }}>
              Share RFP with Vendor
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>
              The vendor will receive an official invitation email with access to review this tender package
            </p>
          </div>
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleShare}
            disabled={saving}
            style={{ minWidth: 140 }}
          >
            <Mail size={15} /> {saving ? 'Sending...' : 'Send Invitation'}
          </button>
        </div>
      }
    >

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Vendor / Company Name <span className="required">*</span></label>
          <input className="form-input" value={form.vendor_name} onChange={set('vendor_name')} placeholder="e.g. Al Habtoor Engineering" />
        </div>
        <div className="form-group">
          <label className="form-label">Contact Person</label>
          <input className="form-input" value={form.contact_person} onChange={set('contact_person')} placeholder="e.g. Mohammed Khan" />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Vendor Email <span className="required">*</span></label>
          <input className="form-input" type="email" value={form.vendor_email} onChange={set('vendor_email')} placeholder="tenders@vendor.com" />
        </div>
        <div className="form-group">
          <label className="form-label">Company Division</label>
          <input className="form-input" value={form.vendor_company} onChange={set('vendor_company')} placeholder="e.g. MEP Division" />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Notes to Vendor (Optional)</label>
        <textarea
          className="form-textarea"
          value={form.notes}
          onChange={set('notes')}
          placeholder="e.g. Please review the attached FIDIC technical and commercial clauses and submit bids before the deadline."
          rows={3}
        />
      </div>
    </Modal>
  );
};

// =============================================
// RFP PREVIEW MODAL (Document Format)
// =============================================
export const RFPPreviewModal = ({ rfp, onClose }) => {
  const sections = [...(rfp?.sections || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const technicalSections = sections.filter(s => s.section_category === 'technical');
  const commercialSections = sections.filter(s => s.section_category === 'commercial');

  const [copiedLink, setCopiedLink] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    setDownloading(true);
    try {
      const title = rfp?.title || 'Request for Proposal';
      const rfpNum = rfp?.rfp_number || 'RFP';
      const projectName = rfp?.project?.name || 'Unassigned';
      const deadline = rfp?.submission_deadline ? formatDate(rfp.submission_deadline) : 'Not specified';
      const contractForm = rfp?.team_inputs?.contract_form || 'FIDIC Red Book';
      const budgetVal = rfp?.budget ? `$${Number(rfp.budget).toLocaleString()}` : (rfp?.team_inputs?.budget_range || 'Subject to Proposal');
      const filename = `${rfpNum.replace(/[^a-zA-Z0-9_-]/g, '_')}_Document.docx`;

      // Build Microsoft Word compliant HTML/XML document with WordSection formatting
      const docHtml = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
<head>
  <meta charset="utf-8">
  <title>${title} - ${rfpNum}</title>
  <!--[if gte mso 9]>
  <xml>
    <w:WordDocument>
      <w:View>Print</w:View>
      <w:Zoom>100</w:Zoom>
      <w:DoNotOptimizeForBrowser/>
    </w:WordDocument>
  </xml>
  <![endif]-->
  <style>
    @page Section1 {
      size: 595.3pt 841.9pt;
      margin: 56.7pt 56.7pt 56.7pt 56.7pt;
      mso-header-margin: 35.4pt;
      mso-footer-margin: 35.4pt;
      mso-paper-source: 0;
    }
    div.Section1 {
      page: Section1;
    }
    body {
      font-family: Calibri, 'Segoe UI', Arial, sans-serif;
      font-size: 11pt;
      line-height: 1.5;
      color: #0f172a;
      background: #ffffff;
      margin: 0;
      padding: 0;
    }
    h1 {
      font-size: 20pt;
      font-weight: bold;
      color: #0B1220;
      margin: 4pt 0 12pt 0;
      line-height: 1.25;
    }
    h2 {
      font-size: 13pt;
      font-weight: bold;
      color: #ffffff;
      background-color: #0B1220;
      padding: 6pt 10pt;
      margin: 20pt 0 10pt 0;
    }
    h2.comm {
      background-color: #0038A7;
    }
    h3 {
      font-size: 12pt;
      font-weight: bold;
      color: #0f172a;
      margin: 14pt 0 4pt 0;
    }
    p {
      margin: 0 0 8pt 0;
      color: #334155;
    }
    .brand-sub {
      font-size: 9pt;
      font-weight: bold;
      color: #2563eb;
      text-transform: uppercase;
      letter-spacing: 1pt;
      margin-bottom: 2pt;
    }
    .badge-doc {
      display: inline-block;
      font-size: 9.5pt;
      font-weight: bold;
      color: #0f172a;
      background-color: #f1f5f9;
      border: 1pt solid #cbd5e1;
      padding: 2pt 8pt;
      margin-bottom: 12pt;
    }
    table.meta-table {
      width: 100%;
      border-collapse: collapse;
      margin: 12pt 0 14pt 0;
      background-color: #f8fafc;
      border: 1pt solid #e2e8f0;
    }
    table.meta-table td {
      padding: 8pt 10pt;
      border: 1pt solid #e2e8f0;
      vertical-align: top;
      width: 25%;
    }
    .meta-lbl {
      font-size: 8pt;
      color: #64748b;
      font-weight: bold;
      text-transform: uppercase;
      margin-bottom: 2pt;
    }
    .meta-val {
      font-size: 10.5pt;
      font-weight: bold;
      color: #0f172a;
    }
    .scope-callout {
      background-color: #f8fafc;
      border-left: 3pt solid #2563eb;
      padding: 8pt 12pt;
      margin: 10pt 0 16pt 0;
      color: #1e293b;
      font-size: 10.5pt;
    }
    .clause-box {
      margin-bottom: 14pt;
      padding-bottom: 10pt;
      border-bottom: 1pt solid #f1f5f9;
    }
    .footer-note {
      margin-top: 30pt;
      padding-top: 10pt;
      border-top: 1pt solid #cbd5e1;
      font-size: 8.5pt;
      color: #94a3b8;
    }
  </style>
</head>
<body>
  <div class="Section1">
    <div class="brand-sub">PropelX Infrastructure Corp — Tender Document</div>
    <h1>${title}</h1>
    <div class="badge-doc">${rfpNum} &nbsp;|&nbsp; STATUS: ${(rfp?.status || 'Draft').toUpperCase()}</div>

    <table class="meta-table">
      <tr>
        <td>
          <div class="meta-lbl">Project</div>
          <div class="meta-val">${projectName}</div>
        </td>
        <td>
          <div class="meta-lbl">Submission Deadline</div>
          <div class="meta-val">${deadline}</div>
        </td>
        <td>
          <div class="meta-lbl">Contract Form</div>
          <div class="meta-val">${contractForm}</div>
        </td>
        <td>
          <div class="meta-lbl">Estimated Budget</div>
          <div class="meta-val">${budgetVal}</div>
        </td>
      </tr>
    </table>

    ${rfp?.description ? `
      <div class="scope-callout">
        <strong>Work Package Overview:</strong><br/>
        ${rfp.description}
      </div>
    ` : ''}

    ${technicalSections.length > 0 ? `
      <h2>Part 1 — Technical Specifications &amp; Scope of Work</h2>
      ${technicalSections.map((sec, i) => `
        <div class="clause-box">
          <h3>1.${i + 1} ${sec.title}</h3>
          <div>${renderMd(sec.content) || '<p><em>Draft pending</em></p>'}</div>
        </div>
      `).join('')}
    ` : ''}

    ${commercialSections.length > 0 ? `
      <h2 class="comm">Part 2 — Commercial, Contractual &amp; Financial Terms</h2>
      ${commercialSections.map((sec, i) => `
        <div class="clause-box">
          <h3>2.${i + 1} ${sec.title}</h3>
          <div>${renderMd(sec.content) || '<p><em>Draft pending</em></p>'}</div>
        </div>
      `).join('')}
    ` : ''}

    <div class="footer-note">
      <table style="width:100%; border:none;">
        <tr>
          <td style="border:none; padding:0; text-align:left; font-size:8.5pt; color:#94a3b8;">
            PropelX Construction &amp; Infrastructure Management Platform
          </td>
          <td style="border:none; padding:0; text-align:right; font-size:8.5pt; color:#94a3b8;">
            Generated: ${new Date().toLocaleDateString('en-GB')}
          </td>
        </tr>
      </table>
    </div>
  </div>
</body>
</html>`;

      const blob = new Blob(['\ufeff', docHtml], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document;charset=utf-8'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleCopySummary = () => {
    const text = `${rfp?.title} (${rfp?.rfp_number})\nProject: ${rfp?.project?.name || ''}\nDeadline: ${rfp?.submission_deadline ? formatDate(rfp.submission_deadline) : 'N/A'}\nTotal Sections: ${sections.length}`;
    navigator.clipboard.writeText(text);
    setCopiedLink(true);
    toast.success('RFP summary copied to clipboard');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 60,
        left: 260,
        right: 0,
        bottom: 0,
        backgroundColor: '#ffffff',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        borderLeft: '1px solid #e2e8f0'
      }}
    >
      {/* 1. Top Action Navigation Bar */}
      <header
        style={{
          height: 64,
          padding: '0 28px',
          background: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)',
          zIndex: 20
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            onClick={onClose}
            className="btn btn-secondary btn-sm"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 600,
              padding: '6px 12px',
              borderRadius: 8
            }}
          >
            <ArrowLeft size={15} /> Back
          </button>

          <div style={{ height: 22, width: 1, background: '#e2e8f0' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                background: '#0B1220',
                color: '#ffffff',
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                padding: '3px 8px',
                borderRadius: 6
              }}
            >
              {rfp?.rfp_number}
            </span>
            <h1
              style={{
                margin: 0,
                fontSize: '1.05rem',
                fontWeight: 700,
                color: '#0B1220',
                maxWidth: 480,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              title={rfp?.title}
            >
              {rfp?.title}
            </h1>
            <span
              style={{
                background: `${STATUS_COLORS[rfp?.status] || '#2563eb'}14`,
                color: STATUS_COLORS[rfp?.status] || '#2563eb',
                border: `1px solid ${STATUS_COLORS[rfp?.status] || '#2563eb'}30`,
                fontSize: '0.7rem',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 12,
                textTransform: 'uppercase'
              }}
            >
              {rfp?.status}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="btn btn-primary btn-sm"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '0.82rem',
              fontWeight: 600,
              padding: '7px 16px',
              borderRadius: 8,
              boxShadow: '0 1px 3px rgba(37, 99, 235, 0.2)'
            }}
          >
            <Download size={15} /> {downloading ? 'Downloading...' : 'Download .docx'}
          </button>
        </div>
      </header>

      {/* 2. Main Document Viewport - Compact & Unified */}
      <main
        style={{
          flex: 1,
          overflowY: 'auto',
          background: '#f8fafc',
          padding: '20px 24px 48px',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ maxWidth: 940, margin: '0 auto' }}>
          {/* Compact Briefing Card */}
          <div
            style={{
              background: '#ffffff',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              padding: '18px 22px',
              marginBottom: 16,
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#2563eb', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Tender Specification
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>•</span>
                  <span style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 600 }}>
                    {sections.length} Clauses Drafted
                  </span>
                </div>
                <h1 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#0B1220', margin: 0, lineHeight: 1.25, letterSpacing: '-0.3px' }}>
                  {rfp?.title}
                </h1>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  background: '#f1f5f9',
                  color: '#334155',
                  border: '1px solid #e2e8f0',
                  padding: '3px 9px',
                  borderRadius: 6,
                  fontSize: '0.74rem',
                  fontWeight: 700
                }}>
                  {rfp?.rfp_number}
                </span>
                <span style={{
                  background: `${STATUS_COLORS[rfp?.status] || '#2563eb'}14`,
                  color: STATUS_COLORS[rfp?.status] || '#2563eb',
                  border: `1px solid ${STATUS_COLORS[rfp?.status] || '#2563eb'}30`,
                  padding: '3px 9px',
                  borderRadius: 6,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  textTransform: 'uppercase'
                }}>
                  {rfp?.status}
                </span>
              </div>
            </div>

            {rfp?.description && (
              <p style={{ margin: '10px 0 14px 0', fontSize: '0.84rem', color: '#475569', lineHeight: 1.5, background: '#f8fafc', padding: '10px 14px', borderRadius: 8, border: '1px solid #edf2f7' }}>
                <strong style={{ color: '#0f172a' }}>Scope Summary: </strong>
                {rfp.description}
              </p>
            )}

            {/* Tight Parameter Strip */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 12,
                marginTop: rfp?.description ? 0 : 12,
                paddingTop: 12,
                borderTop: '1px solid #f1f5f9'
              }}
            >
              <div>
                <span style={{ fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Project</span>
                <strong style={{ color: '#0f172a', display: 'block', fontSize: '0.82rem', marginTop: 1 }}>
                  {rfp?.project?.name || 'Unassigned Project'}
                </strong>
              </div>
              <div>
                <span style={{ fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Submission Deadline</span>
                <strong style={{ color: '#0f172a', display: 'block', fontSize: '0.82rem', marginTop: 1 }}>
                  {rfp?.submission_deadline ? formatDate(rfp.submission_deadline) : 'Not specified'}
                </strong>
              </div>
              <div>
                <span style={{ fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Contract Form</span>
                <strong style={{ color: '#0f172a', display: 'block', fontSize: '0.82rem', marginTop: 1 }}>
                  {rfp?.team_inputs?.contract_form || 'FIDIC Red Book'}
                </strong>
              </div>
              <div>
                <span style={{ fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Estimated Budget</span>
                <strong style={{ color: '#0f172a', display: 'block', fontSize: '0.82rem', marginTop: 1 }}>
                  {rfp?.budget ? `$${Number(rfp.budget).toLocaleString()}` : rfp?.team_inputs?.budget_range || 'Subject to Proposal'}
                </strong>
              </div>
            </div>
          </div>

          {/* Clauses Content - Compact Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Part 1: Technical Specifications */}
            {technicalSections.length > 0 && (
              <div style={{ background: '#ffffff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 20px',
                    background: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <BookOpen size={16} color="#2563eb" />
                    <h2 style={{ margin: 0, fontSize: '0.94rem', fontWeight: 750, color: '#0B1220' }}>
                      Part 1 — Technical Specifications & Scope of Work
                    </h2>
                  </div>
                  <span style={{ fontSize: '0.72rem', color: '#2563eb', background: '#eff6ff', padding: '2px 8px', borderRadius: 10, fontWeight: 600, border: '1px solid #dbeafe' }}>
                    {technicalSections.length} Clauses
                  </span>
                </div>

                <div style={{ padding: '4px 20px 14px' }}>
                  {technicalSections.map((sec, idx) => (
                    <article
                      key={sec.id}
                      style={{
                        padding: '14px 0',
                        borderBottom: idx === technicalSections.length - 1 ? 'none' : '1px solid #f1f5f9'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: '0.76rem', fontWeight: 800, color: '#2563eb' }}>
                            1.{idx + 1}
                          </span>
                          <h3 style={{ fontSize: '0.96rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                            {sec.title}
                          </h3>
                        </div>

                        {sec.ai_generated && (
                          <span style={{ fontSize: '0.66rem', color: '#2563eb', background: '#eff6ff', padding: '1px 6px', borderRadius: 4, border: '1px solid #bfdbfe', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            <Sparkles size={10} /> AI Assisted
                          </span>
                        )}
                      </div>

                      <div
                        style={{ fontSize: '0.84rem', color: '#334155', lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{
                          __html: renderMd(sec.content) || '<em style="color:#94a3b8;">Section content pending drafting.</em>'
                        }}
                      />
                    </article>
                  ))}
                </div>
              </div>
            )}

            {/* Part 2: Commercial & Legal Terms */}
            {commercialSections.length > 0 && (
              <div style={{ background: '#ffffff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 20px',
                    background: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DollarSign size={16} color="#d97706" />
                    <h2 style={{ margin: 0, fontSize: '0.94rem', fontWeight: 750, color: '#0B1220' }}>
                      Part 2 — Commercial, Contractual & Financial Terms
                    </h2>
                  </div>
                  <span style={{ fontSize: '0.72rem', color: '#b45309', background: '#fffbeb', padding: '2px 8px', borderRadius: 10, fontWeight: 600, border: '1px solid #fde68a' }}>
                    {commercialSections.length} Clauses
                  </span>
                </div>

                <div style={{ padding: '4px 20px 14px' }}>
                  {commercialSections.map((sec, idx) => (
                    <article
                      key={sec.id}
                      style={{
                        padding: '14px 0',
                        borderBottom: idx === commercialSections.length - 1 ? 'none' : '1px solid #f1f5f9'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: '0.76rem', fontWeight: 800, color: '#d97706' }}>
                            2.{idx + 1}
                          </span>
                          <h3 style={{ fontSize: '0.96rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                            {sec.title}
                          </h3>
                        </div>

                        {sec.ai_generated && (
                          <span style={{ fontSize: '0.66rem', color: '#b45309', background: '#fffbeb', padding: '1px 6px', borderRadius: 4, border: '1px solid #fde68a', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            <Sparkles size={10} /> AI Assisted
                          </span>
                        )}
                      </div>

                      <div
                        style={{ fontSize: '0.84rem', color: '#334155', lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{
                          __html: renderMd(sec.content) || '<em style="color:#94a3b8;">Section content pending drafting.</em>'
                        }}
                      />
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Document Footer */}
          <div
            style={{
              marginTop: 20,
              padding: '14px 18px',
              borderRadius: 8,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.74rem',
              color: '#64748b'
            }}
          >
            <span>PropelX Construction & Infrastructure Management Platform</span>
            <span>Official Tender Document · Confidential</span>
            <span>Generated on {new Date().toLocaleDateString('en-GB')}</span>
          </div>
        </div>
      </main>
    </div>
  );
};




